import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Radio, PlaneTakeoff, Clock, Globe, ChevronRight, Monitor,
  Search, CheckCircle, X, AlertTriangle, Loader2, Plus,
  Wifi, WifiOff, SkipForward, Pause, Play, RefreshCw, UserCheck, Rocket, AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { gateSessionsAPI, flightsAPI, gatesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useGateSocket } from '../../hooks/useGateSocket';
import { gateSessionStatusKey } from '../../utils/gateSessionStatus';
import { queuePathFor } from '../../utils/queuePath';
import PageHeader from '../../components/UI/PageHeader';
import EmptyState from '../../components/UI/EmptyState';
import StatusBadge from '../../components/UI/StatusBadge';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import { languagePairLabel } from '../../utils/languageLabel';

const STATUS_CFG = {
  pending:             { label: 'Awaiting Acknowledge', tone: 'neutral', accent: 'border-l-gray-400' },
  acknowledged_yellow: { label: 'Acknowledged',          tone: 'yellow',  accent: 'border-l-yellow-400' },
  acknowledged_orange: { label: 'Acknowledged',          tone: 'orange',  accent: 'border-l-orange-500' },
  active:              { label: 'Active',                tone: 'success', accent: 'border-l-emerald-500', pulse: true },
  paused:              { label: 'Paused',                tone: 'warning', accent: 'border-l-amber-400' },
};

// See CR 5.1 grey→yellow/orange→green — gateSessionStatusKey (shared util) resolves
// 'acknowledged' into acknowledged_yellow/acknowledged_orange by time-to-departure.
function statusCfgFor(session) {
  return STATUS_CFG[gateSessionStatusKey(session)] || STATUS_CFG.pending;
}

// A session belongs to this operator if they created it OR acknowledged it —
// both are how /active attributes ownership on the backend. Missing
// acknowledged_by here previously meant a session the operator acknowledged
// (but didn't create — e.g. an auto-synced one) wouldn't sort to the top,
// wouldn't show the "Mine" badge, and — worse — wouldn't count toward "do I
// already have one running," wrongly re-prompting "Start New Session" after
// a fresh login.
function isMineSession(session, userId) {
  return session.created_by === userId || session.acknowledged_by === userId || session.operator?.id === userId;
}

// Maps the backend's validation error `code` (see gateSessions.js POST /) to the
// exact CR-mandated message.
const VALIDATION_MESSAGES = {
  FLIGHT_NOT_FOUND: 'Flight not found. Please check the flight number and try again.',
  GATE_MISMATCH: 'This flight is not currently assigned to the selected gate.',
  NO_PROGRAMME: 'No announcement programme is currently configured for this flight.',
  FLIGHTMS_UNAVAILABLE: 'Flight information is temporarily unavailable. Please try again or contact a supervisor.',
};

// Maps a server validation error onto the form field it belongs to, so the
// modal can show it under that input instead of as a generic error toast.
function fieldErrorFor(err, form) {
  const data = err.response?.data || {};
  const gate = form.gate_code.trim().toUpperCase();
  const flight = form.flight_number.trim().toUpperCase();
  switch (data.code) {
    case 'GATE_NOT_FOUND':
      return { gate: `Gate ${gate} doesn't exist. Please check the gate number.` };
    case 'GATE_NOT_ASSIGNED':
      return { gate: `You are not assigned to gate ${gate}.` };
    case 'FLIGHT_NOT_FOUND':
      return { flight: `Flight ${flight} wasn't found for today. Please check the flight number.` };
    case 'GATE_MISMATCH':
      return {
        gate: data.flightms_gate
          ? `Flight ${flight} departs from gate ${data.flightms_gate}, not ${gate}.`
          : VALIDATION_MESSAGES.GATE_MISMATCH,
      };
    default:
      return {
        general: VALIDATION_MESSAGES[data.code] || data.message ||
          (err.response ? 'Something went wrong. Please try again.' : 'Could not reach the server. Please check your connection.'),
      };
  }
}

// Flight times as on the Changi flight board (Singapore time), e.g. "23 Oct, 07:15 SGT"
function formatSgt(iso) {
  return `${new Date(iso).toLocaleString('en-GB', { timeZone: 'Asia/Singapore', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })} SGT`;
}

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function timeUntil(iso) {
  const diff = new Date(iso) - Date.now();
  if (diff <= 0) return 'Departed';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function FieldError({ message }) {
  return (
    <p role="alert" className="mt-1.5 text-xs text-red-600 flex items-start gap-1">
      <AlertCircle size={13} className="flex-shrink-0 mt-px" />
      {message}
    </p>
  );
}

// ── New Session Form ──────────────────────────────────────────────────────────
// CR 4.1 — the operator types their gate and flight number rather than picking
// from a list; the server resolves/validates both against the real Gate row
// and a live FlightMS lookup (see gateSessions.js POST /).

function NewSessionForm({ onCreated, onClose, mandatory }) {
  const [submitting, setSubmitting] = useState(false);
  // A ref guard, not just the `submitting` state: state updates are async, so a
  // fast double-tap/double-click can fire handleSubmit a second time before
  // React re-renders the button with disabled={submitting} — that raced second
  // call created its own duplicate request (and duplicate error toast) even
  // though the DB-level flight/gate/session checks are otherwise correct.
  const submittingRef = useRef(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookedUpFlight, setLookedUpFlight] = useState(null);
  // Inline validation errors: { gate?, flight?, general? }
  const [errors, setErrors] = useState({});
  // Active gate codes, so Preview can flag an unknown gate before submit. null =
  // couldn't load; the server still validates the gate on Create Session.
  const [gateCodes, setGateCodes] = useState(null);

  useEffect(() => {
    gatesAPI.getAll({ skipErrorToast: true })
      .then(res => setGateCodes(new Set((res.data.data || []).map(g => g.gate_code.toUpperCase()))))
      .catch(() => {});
  }, []);
  // No date is sent: the server works out the flight's next departure in
  // Singapore time (FlightMS dates are SGT). The browser's UTC date was wrong
  // between 00:00 and 08:00 SGT and looked up yesterday's flight.
  // secondary_language is also omitted — the server auto-detects it from the
  // flight's destination (see gateSessions.js POST /).
  const [form, setForm] = useState({ gate_code: '', flight_number: '' });

  // Optional preview only — the server performs its own authoritative FlightMS
  // lookup + validation on submit (flight-not-found / gate-mismatch / no-programme
  // / FlightMS-unavailable), see gateSessions.js POST /.
  async function handleFlightLookup(e) {
    e?.preventDefault();
    const q = form.flight_number.trim().toUpperCase();
    if (!q) return;
    const gate = form.gate_code.trim().toUpperCase();
    const gateError = gate && gateCodes && !gateCodes.has(gate)
      ? `Gate ${gate} doesn't exist. Please check the gate number.`
      : null;
    setLookupLoading(true);
    setLookedUpFlight(null);
    setErrors(gateError ? { gate: gateError } : {});
    try {
      const res = await flightsAPI.lookup(q, undefined, { skipErrorToast: true });
      const flight = res.data.data;
      setLookedUpFlight(flight);
      if (!gateError && gate && flight.gate && String(flight.gate).trim().toUpperCase() !== gate) {
        setErrors({ gate: `Flight ${q} departs from gate ${flight.gate}, not ${gate}.` });
      }
    } catch (err) {
      setErrors(prev => ({ ...prev, ...fieldErrorFor(err, form) }));
    } finally {
      setLookupLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.gate_code.trim() || !form.flight_number.trim()) {
      setErrors({
        ...(!form.gate_code.trim() && { gate: 'Please enter your gate.' }),
        ...(!form.flight_number.trim() && { flight: 'Please enter the flight number.' }),
      });
      return;
    }
    setErrors({});
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const res = await gateSessionsAPI.create({
        gate_code: form.gate_code.trim().toUpperCase(),
        flight_number: form.flight_number.trim().toUpperCase(),
      }, { skipErrorToast: true });
      // Most of the time this gate+flight is already sitting there from the FlightMS
      // auto-sync job — the backend hands that back instead of erroring (find-or-
      // create), so this is the normal path, not just for genuinely new sessions.
      toast.success(
        res.data.found_existing
          ? 'Found your flight — Acknowledge to confirm you are at the gate'
          : 'Session created — Acknowledge to confirm you are at the gate'
      );
      // Go straight into the session detail view — CR 4.3: the operator should see
      // the flight, gate and announcement programme right after validation, not land
      // back on a list.
      onCreated(res.data.data);
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'ALREADY_INITIATED') {
        // Only reaches here when the existing session belongs to another operator
        // (find-or-create already handed back anything accessible as a 200 above) —
        // the modal closes, so this one stays a toast; just refresh the list.
        toast.error(err.response?.data?.message || 'This gate already has an in-progress session');
        onCreated();
        return;
      }
      setErrors(fieldErrorFor(err, form));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  const inputCls = 'w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 focus:bg-white transition-colors';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 px-4 pb-4 sm:pb-0">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
          <div>
            <h2 className="font-bold text-gray-900">Start New Gate Session</h2>
            {mandatory && (
              <p className="text-xs text-gray-400 mt-0.5">Enter your gate and flight number to continue</p>
            )}
          </div>
          {!mandatory && (
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl">
              <X size={18} />
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-5 space-y-4">
          {/* Gate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Gate *</label>
            <input type="text" value={form.gate_code}
              onChange={e => { setErrors(({ gate, general, ...rest }) => rest); setForm(f => ({ ...f, gate_code: e.target.value.toUpperCase() })); }}
              placeholder="e.g. A1" aria-invalid={!!errors.gate}
              className={`${inputCls} uppercase font-mono ${errors.gate ? 'border-red-400 focus:ring-red-400' : ''}`} />
            {errors.gate && <FieldError message={errors.gate} />}
          </div>

          {/* Flight number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Flight Number *</label>
            <input type="text" value={form.flight_number}
              onChange={e => { setLookedUpFlight(null); setErrors(({ flight, general, ...rest }) => rest); setForm(f => ({ ...f, flight_number: e.target.value.toUpperCase() })); }}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleFlightLookup(e))}
              placeholder="e.g. SQ321" aria-invalid={!!errors.flight}
              className={`${inputCls} uppercase font-mono ${errors.flight ? 'border-red-400 focus:ring-red-400' : ''}`} />
            {errors.flight && <FieldError message={errors.flight} />}
          </div>

          {/* Flight preview — informational only; the server does the authoritative
              FlightMS lookup + gate/programme validation on submit. */}
          <div className="bg-blue-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold text-blue-700 uppercase tracking-wide">Flight Preview</p>
              <button type="button" onClick={handleFlightLookup}
                disabled={lookupLoading || !form.flight_number.trim()}
                className="px-3 py-1.5 bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5">
                {lookupLoading ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
                Preview
              </button>
            </div>
            {lookedUpFlight && (
              <div className="bg-white border border-blue-200 rounded-xl px-4 py-3 flex items-start justify-between">
                <div className="space-y-0.5 text-xs">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={13} className="text-green-500" />
                    <span className="font-bold text-gray-900">{lookedUpFlight.flight_number}</span>
                    <span className="text-gray-500">{lookedUpFlight.airline}</span>
                  </div>
                  <div className="text-gray-500 pl-5">
                    <span className="font-medium text-gray-700">{lookedUpFlight.destination}</span>
                    <span className="mx-1.5 text-gray-300">·</span>
                    Dep {formatSgt(lookedUpFlight.departure_time)}
                    {lookedUpFlight.gate
                      ? <><span className="mx-1.5 text-gray-300">·</span>Gate {lookedUpFlight.gate}</>
                      : <span className="text-gray-400"> · Gate TBA</span>}
                  </div>
                </div>
                <button type="button" onClick={() => setLookedUpFlight(null)}
                  className="text-gray-400 hover:text-gray-600 ml-2">
                  <X size={13} />
                </button>
              </div>
            )}
          </div>

          {errors.general && (
            <div role="alert" className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2.5 text-sm">
              <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
              <span>{errors.general}</span>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            {!mandatory && (
              <button type="button" onClick={onClose}
                className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold text-sm hover:bg-gray-50">
                Cancel
              </button>
            )}
            <button type="submit" disabled={submitting}
              className="flex-1 py-3 rounded-xl bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white font-semibold text-sm flex items-center justify-center gap-2">
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {submitting ? 'Creating…' : 'Create Session'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function GateSessionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [formMandatory, setFormMandatory] = useState(false); // true only when auto-opened at login
  const [actionLoading, setActionLoading] = useState(null); // sessionId being actioned
  const autoOpenedRef = useRef(false); // only auto-open the form once per page load

  const loadSessions = useCallback(async () => {
    try {
      const res = await gateSessionsAPI.getActive();
      setSessions(res.data.data || []);
    } catch {
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  useGateSocket({
    onEvent: (evt) => {
      if (['session:created', 'session:status', 'announcement:status', 'gates:assigned'].includes(evt)) {
        loadSessions();
      }
    },
  });

  useEffect(() => {
    loadSessions();
    const t = setInterval(loadSessions, 30000);
    return () => clearInterval(t);
  }, [loadSessions]);

  // CR 4.1: "when an airline user logs in, the user must enter gate + flight number" —
  // but only when they don't already have one running. Without this check, revisiting
  // the Session tab (any navigation away and back remounts this page, resetting
  // autoOpenedRef) would re-prompt for a brand new gate+flight on top of an already
  // in-progress session, which is what this guard prevents — the operator keeps their
  // gate/flight context until they finish (or cancel) the session, not just until they
  // switch tabs. Only fires once per page load, and only for gate operators —
  // supervisors/admins land here to oversee many gates, not to start one themselves.
  useEffect(() => {
    if (loading || autoOpenedRef.current || user?.role !== 'gate_operator') return;
    autoOpenedRef.current = true;
    // An unclaimed auto-created session the operator found via gate+flight
    // entry but hasn't Acknowledged yet won't match isMineSession, so also
    // honor the id the gate queue page (AdminGateQueuePage) stamps into localStorage the moment
    // they land on any session (see useMyActiveGateSession) — same signal
    // the other operator tabs use. That flag is cleared on logout though, so
    // it can't be relied on alone across a real logout/login —
    // isMineSession's acknowledged_by check is what makes this survive that.
    const hasMine = sessions.some(s => isMineSession(s, user.id))
      || !!localStorage.getItem('currentGateSessionId');
    if (!hasMine) {
      setFormMandatory(true);
      setShowNewForm(true);
    }
  }, [loading, user, sessions]);

  async function handlePauseResume(session, e) {
    e.stopPropagation();
    const newStatus = session.status === 'paused' ? 'active' : 'paused';
    if (newStatus === 'paused' &&
        !window.confirm('Pause all announcements for this flight? This should only be used during an operational emergency.')) {
      return;
    }
    setActionLoading(session.id);
    try {
      await gateSessionsAPI.updateStatus(session.id, newStatus);
      setSessions(s => s.map(sess =>
        sess.id === session.id ? { ...sess, status: newStatus } : sess
      ));
      toast.success(newStatus === 'paused' ? 'Session paused' : 'Session resumed');
    } catch {
      toast.error('Failed to update session');
    } finally {
      setActionLoading(null);
    }
  }

  // Acknowledge only records presence — it must never start playback or the timer.
  async function handleAcknowledge(session, e) {
    e.stopPropagation();
    setActionLoading(session.id);
    try {
      const res = await gateSessionsAPI.acknowledge(session.id);
      setSessions(s => s.map(sess => (sess.id === session.id ? res.data.data : sess)));
      toast.success('Acknowledged — press Initiate when ready to start the programme');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to acknowledge session');
    } finally {
      setActionLoading(null);
    }
  }

  // Initiate starts the programme timer, anchored to this moment (not departure time).
  async function handleInitiate(session, e) {
    e.stopPropagation();
    setActionLoading(session.id);
    try {
      await gateSessionsAPI.initiate(session.id);
      toast.success('Initiated — announcement programme is now running');
      // Straight into the queue page — that's where the running programme's
      // times are managed, same as for a session opened from this list.
      navigate(queuePathFor(user?.role, session.gate_id, session.id));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to initiate session');
    } finally {
      setActionLoading(null);
    }
  }

  const filtered = sessions.filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      s.gate?.gate_code?.toLowerCase().includes(q) ||
      s.flight_number?.toLowerCase().includes(q) ||
      s.destination?.toLowerCase().includes(q)
    );
  });

  // Sort: operator's own sessions first, then by departure time
  const sorted = [...filtered].sort((a, b) => {
    const aMine = isMineSession(a, user?.id);
    const bMine = isMineSession(b, user?.id);
    if (aMine && !bMine) return -1;
    if (!aMine && bMine) return 1;
    return new Date(a.departure_time) - new Date(b.departure_time);
  });

  // Deduplicate: keep only the most recent session per gate (highest id = most recently created)
  const deduped = sorted.reduce((acc, session) => {
    const gateId = session.gate_id;
    const existing = acc.find(s => s.gate_id === gateId);
    if (!existing || session.id > existing.id) {
      return [...acc.filter(s => s.gate_id !== gateId), session];
    }
    return acc;
  }, []).sort((a, b) => {
    const aMine = isMineSession(a, user?.id);
    const bMine = isMineSession(b, user?.id);
    if (aMine && !bMine) return -1;
    if (!aMine && bMine) return 1;
    return new Date(a.departure_time) - new Date(b.departure_time);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      {showNewForm && (
        <NewSessionForm
          mandatory={formMandatory}
          onCreated={(created) => {
            setShowNewForm(false);
            setFormMandatory(false);
            if (created?.id) {
              navigate(queuePathFor(user?.role, created.gate_id, created.id));
            } else {
              loadSessions();
            }
          }}
          onClose={() => { setShowNewForm(false); setFormMandatory(false); }}
        />
      )}

      <div className="max-w-lg mx-auto px-4 py-5">

        {/* ── Header ── */}
        <PageHeader
          title="Gate Sessions"
          subtitle={`${sessions.length} active · tap a gate to manage it`}
          actions={
            <>
              <button onClick={loadSessions}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl">
                <RefreshCw size={16} />
              </button>
              <Button variant="primary" icon={Plus} onClick={() => { setFormMandatory(false); setShowNewForm(true); }}>
                New
              </Button>
            </>
          }
        />

        {/* ── Search ── */}
        {sessions.length > 0 && (
          <div className="relative mb-4">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by gate, flight or destination…"
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}

        {/* ── Empty state ── */}
        {sessions.length === 0 && (
          <div className="mb-4">
            <EmptyState
              icon={Radio}
              title="No active gate sessions"
              subtitle="Sessions are created automatically when flights sync, or you can start one manually."
              actionLabel="Start New Session"
              onAction={() => { setFormMandatory(false); setShowNewForm(true); }}
            />
          </div>
        )}

        {/* ── No search results ── */}
        {sessions.length > 0 && deduped.length === 0 && (
          <div className="text-center py-10 text-gray-400">
            <p className="font-medium">No gates match "{search}"</p>
            <button onClick={() => setSearch('')} className="mt-2 text-blue-600 text-sm font-medium">
              Clear search
            </button>
          </div>
        )}

        {/* ── Session list ── */}
        <div className="space-y-3">
          {deduped.map(session => {
            const cfg = statusCfgFor(session);
            const isMine = isMineSession(session, user?.id);
            const isActioning = actionLoading === session.id;
            const queuedCount = (session.announcements || []).filter(
              a => ['pending', 'generating', 'ready'].includes(a.status)
            ).length;
            const playingAnn = (session.announcements || []).find(a => a.status === 'playing');
            const interruptedAnn = (session.announcements || []).find(a => a.status === 'interrupted');
            const countdown = timeUntil(session.departure_time);

            return (
              <Card
                key={session.id}
                onClick={() => navigate(queuePathFor(user?.role, session.gate_id, session.id))}
                interactive
                accent={cfg.accent}
                className="group"
              >
                  <div className="flex-1">
                    {/* ── Top row ── */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        {/* Gate code */}
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-2xl font-black text-gray-900 tracking-tight">
                            {session.gate?.gate_code}
                          </span>
                          {session.gate?.terminal && (
                            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-lg font-medium">
                              T{session.gate.terminal}
                            </span>
                          )}
                          {isMine && (
                            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-lg font-semibold border border-blue-100">
                              Mine
                            </span>
                          )}
                        </div>
                        {/* Flight + status */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <PlaneTakeoff size={12} className="text-gray-400" />
                          <span className="text-sm font-bold text-gray-700">{session.flight_number}</span>
                          {session.airline && <span className="text-xs text-gray-500">{session.airline}</span>}
                          <StatusBadge label={cfg.label} tone={cfg.tone} dot pulse={cfg.pulse} />
                        </div>
                      </div>

                      {/* Departure countdown */}
                      <div className="text-right flex-shrink-0">
                        <p className={`text-xs font-bold ${countdown === 'Departed' ? 'text-red-500' : 'text-gray-500'}`}>
                          {countdown}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">{formatTime(session.departure_time)}</p>
                      </div>
                    </div>

                    {/* ── Flight info ── */}
                    <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-xl px-3 py-2.5 mb-3 text-xs">
                      <div>
                        <p className="text-gray-400 uppercase tracking-wide font-semibold text-[10px] mb-0.5">To</p>
                        <p className="font-semibold text-gray-800 truncate">{session.destination || '—'}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 uppercase tracking-wide font-semibold text-[10px] mb-0.5">Language</p>
                        <p className="font-semibold text-gray-800">
                          {languagePairLabel(session.secondary_language, { short: true, fallback: null })}
                        </p>
                      </div>
                    </div>

                    {/* ── Announcement status ── */}
                    {interruptedAnn ? (
                      <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-3">
                        <Pause size={14} className="text-amber-500 flex-shrink-0" />
                        <p className="text-xs font-semibold text-amber-700">
                          Interrupted — Emergency Pause · <span className="font-normal capitalize">
                            {interruptedAnn.type_label || interruptedAnn.announcement_type?.replace(/_/g, ' ')}
                          </span>
                        </p>
                      </div>
                    ) : playingAnn ? (
                      <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 mb-3">
                        <div className="flex gap-0.5 items-end h-3.5 flex-shrink-0">
                          {[2, 4, 3, 5, 2].map((h, i) => (
                            <div key={i} className="w-0.5 bg-emerald-500 rounded-full animate-bounce"
                              style={{ height: `${h * 3}px`, animationDelay: `${i * 80}ms` }} />
                          ))}
                        </div>
                        <p className="text-xs font-semibold text-emerald-700">
                          Now Playing · <span className="font-normal capitalize">
                            {playingAnn.type_label || playingAnn.announcement_type?.replace(/_/g, ' ')}
                          </span>
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
                        <Radio size={12} className="flex-shrink-0" />
                        {queuedCount > 0
                          ? <span><span className="font-semibold text-blue-600">{queuedCount}</span> announcement{queuedCount > 1 ? 's' : ''} queued</span>
                          : <span>No announcements queued</span>
                        }
                      </div>
                    )}

                    {/* ── Actions ── */}
                    <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                      {session.status === 'pending' && (
                        <Button variant="dark" icon={UserCheck} loading={isActioning}
                          onClick={e => handleAcknowledge(session, e)} className="flex-1">
                          Acknowledge
                        </Button>
                      )}

                      {session.status === 'acknowledged' && (
                        <Button variant="success" icon={Rocket} loading={isActioning}
                          onClick={e => handleInitiate(session, e)} className="flex-1">
                          Initiate
                        </Button>
                      )}

                      {(session.status === 'active' || session.status === 'paused') && (
                        <Button
                          variant={session.status === 'paused' ? 'success' : 'warning'}
                          icon={session.status === 'paused' ? Play : Pause}
                          loading={isActioning}
                          onClick={e => handlePauseResume(session, e)}
                          title={session.status === 'paused' ? 'Resume' : 'Emergency Pause'}
                        >
                          {session.status === 'paused' ? 'Resume' : 'Emergency Pause'}
                        </Button>
                      )}

                      <Button
                        variant="ghost"
                        onClick={() => window.open(`/gate-display/${session.gate_id}`, '_blank', 'noopener,noreferrer')}
                        title="Open display"
                      >
                        <Monitor size={13} />
                      </Button>

                      {(session.status === 'active' || session.status === 'paused') && (
                        <Button variant="primary" onClick={() => navigate(`/operator/queue/${session.gate_id}`)} className="flex-1">
                          Manage Queue <ChevronRight size={13} />
                        </Button>
                      )}
                    </div>
                  </div>
              </Card>
            );
          })}
        </div>

        {/* ── Gate conflict alerts ── */}
        {deduped.some(s => s.auto_created && s.flightms_gate && s.gate?.gate_code &&
          s.flightms_gate.toLowerCase() !== s.gate.gate_code.toLowerCase()) && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
            <AlertTriangle size={15} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 font-medium">
              Some sessions have gate changes from FlightMS. Tap the session to review and accept or override.
            </p>
          </div>
        )}
      </div>
    </>
  );
}
